/**
 * Client-side analytics (01 §9).
 *
 * Only the events the browser alone can see are sent from here — a form being *started*, the
 * offer screen being *shown*. Anything the server can observe (an estimate created, a slot
 * booked) is captured server-side, because a client can be wrong about those and an ad-blocker
 * can drop them entirely.
 *
 * Everything goes to our own API, never to a third party directly (D29 self-hosts PostHog;
 * 08 §2 wants no third-party beacons in a homeowner's browser). Events are batched and flushed
 * with `sendBeacon` on page hide, so leaving the page does not lose the funnel step that
 * explains why they left.
 */

const MAX_BATCH = 25
const FLUSH_MS = 4000

export type ClientEvent =
  | 'estimate_started'
  | 'workspace_offered'
  | 'workspace_opened'
  | 'camera_started'
  | 'photo_added'
  | 'region_selected'
  | 'product_viewed'
  | 'product_tried'
  | 'product_saved'
  | 'product_carted'
  | 'share_link_opened'
  | 'social_shared'

interface Queued {
  event: ClientEvent
  properties: Record<string, unknown>
}

let queue: Queued[] = []
let timer: number | null = null

function post(events: Queued[], useBeacon: boolean): void {
  if (events.length === 0) return
  const body = JSON.stringify({ events })
  const url = '/api/v1/analytics/events'

  if (useBeacon && navigator.sendBeacon) {
    // Fire-and-forget on page hide. A normal fetch is cancelled when the document unloads,
    // which loses exactly the events that explain an abandonment.
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
    return
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'include',
    keepalive: true,
  }).catch(() => {
    // Analytics must never surface an error to a homeowner.
  })
}

function flush(useBeacon = false): void {
  if (timer !== null) {
    window.clearTimeout(timer)
    timer = null
  }
  const batch = queue
  queue = []
  post(batch, useBeacon)
}

export function track(event: ClientEvent, properties: Record<string, unknown> = {}): void {
  queue.push({ event, properties })
  if (queue.length >= MAX_BATCH) {
    flush()
    return
  }
  if (timer === null) timer = window.setTimeout(() => flush(), FLUSH_MS)
}

let installed = false

export function installAnalytics(): void {
  if (installed || typeof document === 'undefined') return
  installed = true
  // `visibilitychange`, not `unload`: iOS Safari does not reliably fire unload, and this is a
  // mobile-first product (10 §3).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true)
  })
  window.addEventListener('pagehide', () => flush(true))
}
